import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { API_BASE } from '../../constants/general.constants';

/**
 * Deployment-wide bootstrap info from the authenticated GET /api/deployment-info
 * endpoint (SCRUM-782). These values are deployment-global (not project-scoped),
 * so the app fetches them ONCE after authentication and exposes them as signals.
 *
 * - deploymentId: stable, human-readable deployment identifier (APP_ENVIRONMENT
 *   based). Read this from the server rather than self-deriving, so local ehq-web
 *   run against the dev backend reports `dev` instead of `localhost`.
 * - secretSources: secret-source types available on this deployment (SCRUM-783
 *   drives the secret-source dropdown from this).
 * - cloudProvider: the deployment's own app-storage provider (informational only;
 *   never gate the BYOC object-storage connection dropdown on this).
 * - emailEnabled: whether SMTP is configured deployment-wide.
 */
export interface DeploymentInfo {
  deploymentId: string;
  secretSources: string[];
  cloudProvider: string | null;
  emailEnabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class DeploymentInfoService {
  private http = inject(HttpClient);

  readonly #info = signal<DeploymentInfo | null>(null);
  // Shared in-flight request so concurrent callers don't each issue a GET; cleared
  // on settle so a retry is possible after a failed load.
  #inFlight: Promise<DeploymentInfo | null> | null = null;

  /** Stable deployment id from the server; `unknown` until loaded. */
  readonly deploymentId = computed(() => this.#info()?.deploymentId ?? 'unknown');
  /** Deployment app-storage provider (informational only); null until loaded. */
  readonly cloudProvider = computed(() => this.#info()?.cloudProvider ?? null);
  /** Whether SMTP email is configured deployment-wide; false until loaded. */
  readonly emailEnabled = computed(() => this.#info()?.emailEnabled ?? false);
  /** Secret-source types available on this deployment; empty until loaded. */
  readonly secretSources = computed(() => this.#info()?.secretSources ?? []);
  /** True once deployment-info has been successfully fetched. */
  readonly loaded = computed(() => this.#info() !== null);

  /**
   * Fetch /api/deployment-info once and cache it into the signals. Idempotent:
   * a second call returns the cached value without re-fetching. Call after auth
   * (the endpoint is authenticated). Non-critical — callers should treat a
   * rejection as "use defaults" rather than blocking app startup.
   */
  async load(): Promise<DeploymentInfo | null> {
    const existing = this.#info();
    if (existing) return existing;
    if (this.#inFlight) return this.#inFlight;

    this.#inFlight = firstValueFrom(
      this.http.get<DeploymentInfo>(`${API_BASE}/deployment-info`),
    )
      .then((info) => {
        this.#info.set(info);
        return info;
      })
      .finally(() => {
        this.#inFlight = null;
      });
    return this.#inFlight;
  }
}
